/** Optional numeric fields — omitted keys count as 0; negative live counters clamp to 0. */
export function toAnalyticsCount(value) {
  if (value == null) return 0;
  return value < 0 ? 0 : value;
}

/** Current month as `YYYY-MM`. */
export function getCurrentAnalyticsMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

/** `YYYY-MM` → e.g. "June 2026". */
export function formatAnalyticsMonthLabel(month) {
  const [year, monthNum] = month.split("-");
  return new Date(Number(year), Number(monthNum) - 1, 1).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric"
  });
}

/** Recent months for analytics filters: `{ value: YYYY-MM, label }` newest first. */
export function buildAnalyticsMonthOptions(count = 12) {
  const options = [];
  const now = new Date();
  for (let i = 0; i < count; i += 1) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
    options.push({
      value,
      label: formatAnalyticsMonthLabel(value),
      isCurrent: i === 0
    });
  }
  return options;
}

/** `YYYY-MM-DD` → e.g. "Jul 22". */
export function formatAnalyticsDayLabel(date) {
  const [year, monthNum, day] = date.split("-");
  return new Date(Number(year), Number(monthNum) - 1, Number(day)).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric"
  });
}

export function humanizeNodeId(nodeId) {
  return nodeId
    .replace(/^nd_/, "")
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/** Format ms as `50s` or `2m 5s`. */
export function formatDurationFromMs(ms) {
  const totalSec = Math.max(0, Math.round(toAnalyticsCount(ms) / 1000));
  if (totalSec < 60) return `${totalSec}s`;
  const minutes = Math.floor(totalSec / 60);
  const seconds = totalSec % 60;
  return seconds ? `${minutes}m ${seconds}s` : `${minutes}m`;
}

function ratePercent(part, whole) {
  if (!whole) return 0;
  return Math.round((part / whole) * 100);
}

/** Sum session rows whose `date` falls in `month` (`YYYY-MM`). */
export function aggregateSessionStats(sessionStats, month) {
  return sessionStats
    .filter((row) => row.date.startsWith(month))
    .reduce(
      (acc, row) => ({
        started: acc.started + toAnalyticsCount(row.started),
        completed: acc.completed + toAnalyticsCount(row.completed),
        abandoned: acc.abandoned + toAnalyticsCount(row.abandoned),
        handoff: acc.handoff + toAnalyticsCount(row.handoff),
        migratedRestart: acc.migratedRestart + toAnalyticsCount(row.migratedRestart),
        durationMsSum: acc.durationMsSum + toAnalyticsCount(row.durationMsSum),
        durationCount: acc.durationCount + toAnalyticsCount(row.durationCount)
      }),
      {
        started: 0,
        completed: 0,
        abandoned: 0,
        handoff: 0,
        migratedRestart: 0,
        durationMsSum: 0,
        durationCount: 0
      }
    );
}

export function computeSessionMetrics(totals) {
  const {
    started,
    completed,
    abandoned,
    handoff,
    migratedRestart,
    durationMsSum,
    durationCount
  } = totals;

  return {
    started,
    completed,
    abandoned,
    handoff,
    migratedRestart,
    completionRate: ratePercent(completed, started),
    abandonedRate: ratePercent(abandoned, started),
    handoffRate: ratePercent(handoff, started),
    avgDurationLabel: durationCount
      ? formatDurationFromMs(durationMsSum / durationCount)
      : "—"
  };
}

export function computeNodePassRate(entries, exits) {
  if (!entries) return null;
  return Math.round((exits / entries) * 100);
}

/**
 * Node performance rows from `nodeStats`, aggregated by month.
 * Sorted by activity (entries + exits) desc so active steps surface first.
 */
export function buildNodePerformanceRows(analytics, month) {
  const byNode = {};

  analytics.nodeStats.forEach((row) => {
    if (!row.date.startsWith(month)) return;

    const { nodeId } = row;
    if (!byNode[nodeId]) {
      byNode[nodeId] = {
        nodeId,
        label: humanizeNodeId(nodeId),
        entries: 0,
        exits: 0,
        fallbacks: 0,
        errors: 0,
        durationMsSum: 0,
        durationCount: 0
      };
    }
    byNode[nodeId].entries += toAnalyticsCount(row.entries);
    byNode[nodeId].exits += toAnalyticsCount(row.exits);
    byNode[nodeId].fallbacks += toAnalyticsCount(row.fallbacks);
    byNode[nodeId].errors += toAnalyticsCount(row.errors);
    byNode[nodeId].durationMsSum += toAnalyticsCount(row.durationMsSum);
    byNode[nodeId].durationCount += toAnalyticsCount(row.durationCount);
  });

  return Object.values(byNode)
    .map((row) => ({
      ...row,
      avgDurationLabel: row.durationCount
        ? formatDurationFromMs(row.durationMsSum / row.durationCount)
        : "—",
      passRate: computeNodePassRate(row.entries, row.exits)
    }))
    .sort(
      (a, b) =>
        b.entries + b.exits - (a.entries + a.exits) || b.entries - a.entries
    );
}

/** Live occupancy rows sorted by count desc. */
export function buildHeatMapList(analytics) {
  return analytics.nodeHeatMap
    .map((row) => ({
      nodeId: row.nodeId,
      label: humanizeNodeId(row.nodeId),
      liveCount: toAnalyticsCount(row.count)
    }))
    .sort((a, b) => b.liveCount - a.liveCount);
}

/** Per-day session rows for the selected month, newest first. */
export function buildDailySessionRows(sessionStats, month) {
  return sessionStats
    .filter((row) => row.date.startsWith(month))
    .map((row) => {
      const started = toAnalyticsCount(row.started);
      const completed = toAnalyticsCount(row.completed);
      const abandoned = toAnalyticsCount(row.abandoned);
      const handoff = toAnalyticsCount(row.handoff);
      const durationCount = toAnalyticsCount(row.durationCount);
      const durationMsSum = toAnalyticsCount(row.durationMsSum);

      return {
        date: row.date,
        dayLabel: formatAnalyticsDayLabel(row.date),
        started,
        completed,
        abandoned,
        handoff,
        completionRate: ratePercent(completed, started),
        avgDurationLabel: durationCount
          ? formatDurationFromMs(durationMsSum / durationCount)
          : "—"
      };
    })
    .sort((a, b) => b.date.localeCompare(a.date));
}

export function buildAiInsightMessage(metrics, nodeRows, liveSessionCount) {
  if (!metrics?.started) {
    return liveSessionCount
      ? `${liveSessionCount} session${liveSessionCount === 1 ? "" : "s"} currently live. Run more conversations to unlock completion trends.`
      : "No sessions recorded for this month yet. Publish and trigger the workflow to start collecting analytics.";
  }

  const errorNode = nodeRows
    .filter((node) => node.errors > 0)
    .sort((a, b) => b.errors - a.errors)[0];
  if (errorNode) {
    return `${errorNode.errors} error${errorNode.errors === 1 ? "" : "s"} at ${errorNode.label}. Review that step — your completion rate is ${metrics.completionRate}%.`;
  }

  const fallbackNode = nodeRows
    .filter((node) => node.fallbacks > 0)
    .sort((a, b) => b.fallbacks - a.fallbacks)[0];
  if (fallbackNode) {
    return `${fallbackNode.fallbacks} fallback${fallbackNode.fallbacks === 1 ? "" : "s"} at ${fallbackNode.label}. Tighten reply matching to improve the ${metrics.completionRate}% completion rate.`;
  }

  if (metrics.abandoned > 0) {
    return `${metrics.abandoned} of ${metrics.started} sessions were abandoned (${metrics.abandonedRate}%). Completion is ${metrics.completionRate}% — review early steps where users leave.`;
  }

  return `Your workflow has a ${metrics.completionRate}% completion rate across ${metrics.started} started sessions. Keep monitoring for opportunities to optimize.`;
}
